import { useEffect, useState } from 'react';
import { execApi } from '../../../services/api';
import { T, ils, ils0, num, pct2, marginColor } from '../../../theme/tokens';
import type { ProfitData } from './types';
import type { ProfitFilterSelection } from '../../../services/api';
import { Card, Title, Scroll, Th, Td, Row, TotalRow, Footnote, Blocked } from './parts/Panel';
import { Tile } from './parts/Tile';
import { Donut } from './parts/Donut';
import { ForecastBar, TrendBars, Bridge } from './parts/Charts';
import { Filters, NotFiltered } from './parts/Filters';
import { monthToDate } from './parts/DateRange';
import type { Range } from './parts/DateRange';
import type { Options as FilterOptions } from './parts/Filters';

/* The by-service row that holds doctor closures. Mirrors CLOSURE_SERVICE in
   exec_profit.py: its `n` is closures, not meetings, so the meetings total skips it. */
const CLOSURE_SERVICE = 'סגירת מנוי ע״י כונן';

/* Avi's financial dashboard — the screen he specced tile by tile on 14/10/2026.
   This file is the LAYOUT and the data binding; every panel's drawing lives in
   `parts/`. Nothing here computes a business number: `GET /api/exec/profit` is the
   only source, and the one arithmetic allowed on this side is the month-over-month
   delta, taken from the same `trend` series the charts are drawn from so the arrow
   and the chart can never tell different stories.

   🔴 Three things on this screen are deliberate and must survive a redesign:
     · a blocked number says WHY instead of showing a plausible figure
     · the "?" beside a figure carries its definition, because a definition in a
       document gets read once and a question mark gets asked every time
     · no decorative chart. Six of the eight tiles have no monthly history, so six
       of the eight carry no sparkline. */

const GRID = (min: number): React.CSSProperties => ({
  display: 'grid', gap: 14, alignItems: 'start',
  gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`,
});

function PageBtn({ disabled, onClick, children }: {
  disabled: boolean; onClick: () => void; children: React.ReactNode;
}) {
  return (
    <button
      type="button" disabled={disabled} onClick={onClick}
      style={{
        fontSize: 12, padding: '4px 12px', borderRadius: 6, cursor: disabled ? 'default' : 'pointer',
        border: `1px solid ${T.border}`, background: T.card,
        color: disabled ? T.inkFaint : T.ink,
      }}
    >
      {children}
    </button>
  );
}

export function ExecProfit() {
  /* Month-to-date on open — Avi, 14/10: "נפתח על החודש · מה היה מתחילת החודש עד
     אותו יום". The window is the one piece of state the whole screen hangs on. */
  const [range, setRange] = useState<Range>(() => monthToDate());
  const [data, setData] = useState<ProfitData | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [sel, setSel] = useState<ProfitFilterSelection>({});
  const [options, setOptions] = useState<FilterOptions | null>(null);
  // Page of the by-therapist table, ten a page (Ohad, 02/11: "נציג 10 כל פעם
  // בדפים פנימיים"). Back to the first page whenever the payload changes.
  const [tPage, setTPage] = useState(0);
  useEffect(() => { setTPage(0); }, [data]);

  /* The option lists are fetched once and never again: they are the distinct values
     of four columns over six months, and they do not change while someone reads the
     screen. A failure here leaves the dropdowns empty rather than blanking the
     dashboard — a filter nobody can open is an inconvenience, a dashboard that will
     not load is an outage. */
  useEffect(() => {
    let alive = true;
    execApi.getProfitFilterOptions()
      .then((r) => { if (alive) setOptions(r.data as FilterOptions); })
      .catch(() => { if (alive) setOptions(null); });
    return () => { alive = false; };
  }, []);

  /* `selKey` and not `sel` in the dependency list: `sel` is a fresh object on every
     render, so depending on it directly would refetch in a loop. */
  const selKey = JSON.stringify(sel);
  const rangeKey = `${range.from}..${range.to}`;
  useEffect(() => {
    let alive = true; setLoading(true); setErr('');
    const [from, to] = rangeKey.split('..');
    execApi.getProfit({ from, to }, JSON.parse(selKey))
      .then((r) => { if (alive) setData(r.data as ProfitData); })
      .catch((e) => { if (alive) setErr(e?.response?.data?.detail || 'טעינה נכשלה'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [rangeKey, selKey]);

  if (loading && !data) return <div style={{ fontSize: 14, color: T.inkSoft, padding: 24 }}>טוען…</div>;
  if (err) return <div style={{ fontSize: 14, color: T.red, padding: 24 }}>{err}</div>;
  if (!data) return null;
  const d = data;

  /* Deltas and sparklines both come out of `trend`, and out of nothing else. */
  const pct = (now: number, before?: number) =>
    before && before > 0 ? ((now - before) / before) * 100 : undefined;

  /* 🔴 The sparklines end on the last WHOLE month. `trend` carries the current month
     too, and on 02/11 that point was two days of November drawn after all of October —
     a cliff at the end of every line that said "collapse" and meant "it's the 2nd"
     (Ohad, 02/11). The calendar month is taken locally: toISOString() is UTC and
     turns the 1st, before 02:00, into the previous month. */
  const now = new Date();
  const thisMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const whole = d.trend.filter((t) => t.month !== thisMonth);
  const revSeries = whole.map((t) => t.revenue);
  const costSeries = whole.map((t) => t.therapist_cost);
  /* Gross is earned minus cost, never billed minus cost — see `recognized_revenue`. */
  const grossSeries = whole.map((t) => t.recognized - t.therapist_cost);
  const meetSeries = whole.map((t) => t.meetings);
  const rppSeries = whole.map((t) => (t.patients ? t.revenue / t.patients : 0));
  const noshowSeries = whole.map((t) => t.noshow_loss);
  /* The forecast is where THIS month will land, so its history is where the past
     months landed — the same series as the revenue tile — and its comparison is the
     last whole month's actual total, not a slice of it. */
  const lastWhole = whole.length ? whole[whole.length - 1] : undefined;
  /* 🔴 Every tile arrow compares against the SAME stretch of the previous month
     (`prev_*`, from the server), not against `trend`'s whole month. On 1/11 the old
     base drew one day of November against all of October — a 97% "collapse" — and
     the cost arrow set payroll against a sum of per-meeting rates (found 01/11/2026).
     `trend` still draws the sparklines, and nothing else. */
  const cmp = d.prev_meetings > 0;
  const prevCost = cmp && d.prev_therapist_cost > 0 ? d.prev_therapist_cost : undefined;
  const prevGross = prevCost !== undefined ? d.prev_recognized_revenue - prevCost : undefined;
  const prevRpp = cmp && d.prev_revenue_per_patient ? d.prev_revenue_per_patient : undefined;

  /* The no-show rate Avi asked to see beside the money ("בשילוב של כמה אחוזים של
     No-show יש לי, ומה ההפסד הכספי"). `meetings` counts completed meetings only, so
     the denominator is completed plus missed — the meetings that were supposed to
     happen. Since 02/11 it lives in the "?" — the line under the number is gone. */
  const noshowBase = d.meetings + d.noshow_meetings;
  const noshowRate = noshowBase > 0 ? (d.noshow_meetings / noshowBase) * 100 : 0;

  /* The per-meeting cost the tables are built from, totalled once so the by-payer
     footnote and its total row cannot disagree. */
  const payerCost = d.by_payer.reduce((a, r) => a + (r.unpriced ? 0 : r.cost), 0);
  /* Avi asked for "הכנסה ממוצעת למטפל" beside the per-patient figures (P17). It is
     the one LTV number that had no home until the per-therapist table existed. */
  const revPerTherapist = d.by_therapist.length
    ? d.by_therapist.reduce((a, t) => a + t.revenue, 0) / d.by_therapist.length
    : 0;

  /* What the backend says a given panel could NOT be narrowed by. Rendered as a
     badge on the panel itself: four filters against thirteen queries do not all
     land, and a panel that quietly stayed whole while its neighbours narrowed is a
     wrong number wearing the costume of a right one. */
  const missed = (panel: string) => d.filters_not_applied?.[panel];

  return (
    <div dir="rtl" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

      <Filters
        range={range} onRange={setRange}
        options={options} selection={sel} onSelection={setSel} loading={loading}
      />

      {/* No banners above the tiles. Two lived here — private/donation meetings
          (removed 01/11) and therapists missing from payroll (removed 02/11) — and
          Ohad took both out: "זה לא מעניין". Banners are not added without asking. */}

      {/* ── the tile row ──────────────────────────────────────────────────────
          "שיעור רווח גולמי" is the second line of the gross-profit tile and not a
          tile of its own — Avi, 14/10: "למה צריך רובריקה נפרדת? אתה יכול להראות את
          זה באותה טבלה". "עלויות ישירות נוספות" and "רווח תפעולי (EBITDA)" are off
          the screen entirely until the CFO supplies a monthly figure. */}
      <div style={GRID(176)}>
        <Tile
          icon="◉" iconColor={T.blue} label="הכנסות כוללות" value={ils0(d.revenue)}
          delta={cmp ? pct(d.revenue, d.prev_revenue) : undefined} spark={revSeries}
          ask={`לפי החשבונית: כל אינטייק מנהל תיק שבוצע × המנוי האבחוני המלא, ומעקבים × תעריף. המנוי נספר כולו ברגע שאינטייק מנהל התיק בוצע — כך יוצא הדיווח לקופה בסוף החודש — ולכן הסכום כולל גם את החלק שעוד לא הורווח (${ils0(d.billed_not_recognized)} בטווח הזה). פרטי ותרומות אינם בפנים — התשלום עליהם אינו נקשר לפגישה (${num(d.excluded_meetings)} פגישות).`}
        />
        <Tile
          icon="◆" iconColor="#8B5CF6" label="עלויות מטפלים" value={ils0(d.therapist_cost)}
          delta={pct(d.therapist_cost, prevCost)} invert spark={costSeries}
          ask="ממנוע השכר, וכולל בונוסים, כוננות, שעות לא-קליניות ותשלום על אי-הגעה. בטווח חלקי או בסינון — עלות החודש מתחלקת לפי חלק הפגישות (בעלות לפגישה) שבתוך הטווח והסינון; בחודש הנוכחי לפי התחזית של מנוע השכר לחודש כולו. חודש מלא בלי סינון = מנוע השכר בדיוק."
        />
        <Tile
          icon="◐" iconColor={T.green} label="רווח גולמי" value={ils0(d.gross)}
          delta={pct(d.gross, prevGross)} spark={grossSeries}
          ask={`ההכנסה שהוכרה פחות עלות המטפלים והכוננים. "הוכר" אינו החשבונית: המנוי האבחוני מוכר שליש באינטייק מנהל התיק, ושני שליש כשהפסיכיאטר ראה את המטופל או כשכונן סגר מנוי שלא הגיע לפסיכיאטר (${num(d.oncall_closures)} סגירות בטווח הזה, ${ils0(d.oncall_closure_revenue)}). מעקבים במלואם. ההפרש מהחשבונית, ${ils0(d.billed_not_recognized)}, הוא מנויים שחויבו ועוד מחכים לפסיכיאטר. עלויות המשרד, השכירות והספקים אינן כלולות.`}
        />
        {d.forecast_meetings > 0 && (
          <Tile
            icon="▲" iconColor={T.green} label="תחזית הכנסות לסוף החודש" value={ils0(d.forecast_revenue)}
            delta={lastWhole ? pct(d.forecast_revenue, lastWhole.revenue) : undefined} spark={revSeries}
            ask={`${num(d.forecast_meetings)} פגישות כבר קבועות ביומן להמשך החודש. שלוש מדרגות: (1) במחיר מלא הן ${ils0(d.forecast_at_face - d.revenue)}. (2) לא כולן יתקיימו — כל סוג מוכפל בשיעור המימוש של שלושת החודשים האחרונים (אינטייק מנ"ת ${(d.realization.cm_intake ?? 0).toFixed(2)}%, פסיכיאטר ${(d.realization.psy_intake ?? 0).toFixed(2)}%, מעקב פסיכיאטר ${(d.realization.psy_fu ?? 0).toFixed(2)}%, מעקב מנ"ת ${(d.realization.cm_fu ?? 0).toFixed(2)}%) — יחד עם מה שכבר בוצע זה ${ils0(d.forecast_booked)}, וזו רצפה, לא תחזית. (3) ב-${d.forecast_day} לחודש היומן עוד לא מלא: בשלושת החודשים האחרונים רק ${(d.fill.cm_fu ?? 100).toFixed(2)}% ממעקבי מנ"ת של שאר החודש, ${(d.fill.psy_fu ?? 100).toFixed(2)}% ממעקבי הפסיכיאטר, ${(d.fill.cm_intake ?? 100).toFixed(2)}% מאינטייקי מנ"ת ו-${(d.fill.psy_intake ?? 100).toFixed(2)}% מאינטייקי הפסיכיאטר כבר היו קבועים ביום הזה. הקבוע מחולק בשיעור הזה, וככל שהחודש מתקדם השיעור עולה ל-100% והתחזית מתכנסת לבפועל. החץ משווה לסך ההכנסות של ${lastWhole?.month ?? 'החודש הקודם'} כולו (${ils0(lastWhole?.revenue ?? 0)}), והקו הוא איפה נחתו ששת החודשים האחרונים.`}
          />
        )}
        {/* Avi asked for the no-show loss as a tile up top as well as a panel below:
            "אני שם לך רובריקה למעלה... בשילוב של כמה אחוזים של No-show יש לי, ומה
            ההפסד הכספי". The money is the headline, the rate is the second line. */}
        {d.noshow_meetings > 0 && (
          <Tile
            icon="⊘" iconColor={T.red} label="הפסד מאי-הגעה" value={ils0(d.noshow_loss)}
            delta={cmp && d.prev_noshow_loss > 0 ? pct(d.noshow_loss, d.prev_noshow_loss) : undefined}
            invert spark={noshowSeries}
            ask={(d.noshow_is_measured
              ? 'ממנוע התשלומים: מה ששולם בפועל לכל מטפל על אי-הגעה, לפי האחוז שמוגדר לו.'
              : 'אומדן: 35% מעלות המטפל לאותה פגישה — מנוע התשלומים לא ענה.')
              + ` ${pct2(noshowRate)} מהפגישות (${num(d.noshow_meetings)} אי-הגעות מתוך ${num(noshowBase)} שהיו אמורות להתקיים: בוצעו ועוד לא-הגיעו).`
              + (cmp && d.prev_noshow_loss > 0 ? ` באותם ימים בחודש הקודם: ${ils0(d.prev_noshow_loss)}.` : '')}
          />
        )}
        <Tile
          icon="◉" iconColor={T.blue} label="מטופלים פעילים" value={num(d.patients_active)}
          ask={`עברו אינטייק מנהל תיק והתיק לא נסגר — ההגדרה של אוהד, 07/10. שלוש חלופות שנדחו: נראו בחודש ${num(d.patients)} · נראו ב-90 יום ${num(d.patients_seen_90d)} · תיק פתוח בלי אינטייק ${num(d.patients_case_open)}. (והדגל active=TRUE מחזיר 19,308 — חסר משמעות.) אין השוואה לחודש קודם, כי המערכת שומרת רק את הסטטוס של היום: תאריך סיום הטיפול חסר אצל 944 מהסגורים ומיושן אצל 1,071 מהפעילים (נמדד 02/11), ושחזור "כמה היו פעילים לפני חודש" היה טועה במאות.`}
        />
        <Tile
          icon="▦" iconColor={T.blue} label="מספר פגישות חודשיות" value={num(d.meetings)}
          delta={cmp ? pct(d.meetings, d.prev_meetings) : undefined} spark={meetSeries}
          ask="רק פגישות שבוצעו, ועד הרגע הזה — פגישות עתידיות אינן נספרות. אי-הגעה אינה כאן: היא עולה כסף ומזכה בתשלום חלקי, ויושבת באריח שלה."
        />
        <Tile
          icon="◎" iconColor={T.orange} label="הכנסה ממוצעת למטופל" value={ils0(d.revenue_per_patient)}
          delta={pct(d.revenue_per_patient, prevRpp)} spark={rppSeries}
          ask={`מחולק ב-${num(d.patients)} שנראו החודש. אם המכנה היה "מטופלים פעילים" (${num(d.patients_active)}) המספר היה ${ils0(d.revenue / Math.max(d.patients_active, 1))}. איזה מהם?`}
        />
      </div>

      {/* ── where the money came from, how the month is shaped, where it lands ── */}
      <div style={GRID(300)}>
        <Card>
          <Title badge={<NotFiltered names={missed('by_payer')} />}>הכנסות לפי קופה</Title>
          <Donut rows={d.by_payer} total={d.revenue} />
        </Card>

        {/* Only while something is still booked. On a finished period the forecast
            equals the actual, and the panel renders a two-series legend over a
            single line — full visual authority, nothing forecast (found 19/10). */}
        {d.forecast_meetings > 0 && (
          <Card>
            <Title
              badge={<NotFiltered names={missed('forecast')} />}
              note={d.month}
              ask="פס אחד, שלוש ודאויות. כהה: מה שכבר בוצע. בינוני: מה שכבר קבוע ביומן, אחרי הנחה על מי שבדרך כלל לא מגיע. מפוספס: מה שעוד לא נקבע, אבל לפי שלושת החודשים האחרונים ייקבע תוך כדי החודש — ביום הזה בחודש היומן בדרך כלל מלא רק בחלקו. הקו השחור הוא איפה נחת החודש הקודם. ככל שהחודש מתקדם הקטע המפוספס מתכווץ עד שנעלם."
            >
              תחזית הכנסות
            </Title>
            <ForecastBar
              revenue={d.revenue} booked={d.forecast_booked} forecast={d.forecast_revenue}
              reference={lastWhole ? { label: lastWhole.month, value: lastWhole.revenue } : undefined}
            />
          </Card>
        )}

        <Card>
          <Title badge={<NotFiltered names={missed('trend')} />} note="עלויות נוספות יתווספו כשיגיעו">הכנסות ועלויות — מגמה חודשית</Title>
          <TrendBars trend={d.trend} revenue={d.revenue} cost={d.therapist_cost} />
        </Card>
      </div>

      {/* ── what was paid out for meetings nobody attended ────────────────────
          The breakdown sits under the headline because an unattended psychiatrist
          intake and an unattended ten-minute follow-up are not the same loss. */}
      <div style={GRID(460)}>
      {d.noshow_meetings > 0 && (
        <Card flush>
          <Title flush badge={<NotFiltered names={missed('noshow')} />} note={d.month}>הפסד מאי-הגעה — לפי סוג פגישה</Title>
          <Scroll min={340}>
            <thead>
              <tr>
                <Th>סוג פגישה</Th>
                <Th align="center">אי-הגעה</Th>
                <Th align="end">שולם בכל זאת</Th>
              </tr>
            </thead>
            <tbody>
              {d.noshow_by_service.map((r) => (
                <Row key={r.service}>
                  <Td strong>{r.service}</Td>
                  <Td align="center" soft>{num(r.n)}</Td>
                  <Td align="end" strong color={T.red}>{ils(r.cost)}</Td>
                </Row>
              ))}
            </tbody>
            <tfoot>
              <TotalRow>
                <Td strong>סה״כ</Td>
                <Td align="center" strong>{num(d.noshow_meetings)}</Td>
                <Td align="end" strong color={T.red}>{ils(d.noshow_loss)}</Td>
              </TotalRow>
            </tfoot>
          </Scroll>
          <Footnote pad>
            {pct2((d.noshow_loss / Math.max(d.gross, 1)) * 100)} מהרווח הגולמי.
            {' '}לפי 35% מעלות המטפל לאותה פגישה — המספר היחיד במסך שמגיע ממשפט ולא ממדידה.
          </Footnote>
        </Card>
      )}

      {/* ── the bridge from revenue to what is left of it ───────────────────── */}
      <Card>
        <Title note={d.month}>גשר רווח גולמי</Title>
        {/* The bridge starts from what was EARNED, so its three bars add up. Starting
            from the invoice would leave a gap nobody could name. */}
        <Bridge revenue={d.recognized_revenue} revenueLabel="הכנסה שהוכרה"
                cost={d.therapist_cost} gross={d.gross} pct={d.gross_pct} />
      </Card>

      </div>

      {/* ── profitability at three grains, side by side ───────────────────────
          Stacked vertically these read as three unrelated screens; side by side
          they read as one question asked at three levels of detail. */}
      <div style={GRID(460)}>
        <Card flush>
          <Title flush badge={<NotFiltered names={missed('by_payer')} />}>רווחיות לפי קופה</Title>
          <Scroll min={300}>
            <thead>
              <tr>
                <Th>קופה</Th>
                <Th align="end">הכנסות</Th>
                <Th align="end">הוכר</Th>
                <Th align="end">עלות</Th>
                <Th align="end">רווח</Th>
                <Th align="center">מרווח</Th>
              </tr>
            </thead>
            <tbody>
              {d.by_payer.map((r) => (
                <Row key={r.payer}>
                  <Td strong>{r.payer}</Td>
                  <Td align="end" title={r.unpriced ? 'אין תעריף — שולם בכרטיס שאינו נקשר לפגישה' : undefined}>
                    {r.unpriced ? <span style={{ color: T.inkFaint }}>—</span> : ils(r.revenue)}
                  </Td>
                  {/* Billed beside earned: profit is measured against the second. */}
                  <Td align="end" title="שליש מהמנוי באינטייק מנהל התיק, שני שליש באינטייק הפסיכיאטר או בסגירת כונן, מעקבים במלואם">
                    {r.unpriced ? '—' : ils(r.recognized)}
                  </Td>
                  <Td align="end" soft>{r.unpriced ? '—' : ils(r.cost)}</Td>
                  <Td align="end" strong>{r.unpriced ? '—' : ils(r.profit)}</Td>
                  <Td align="center" strong color={r.unpriced ? T.inkFaint : marginColor(r.margin)}>
                    {r.unpriced ? '—' : `${pct2(r.margin)}`}
                  </Td>
                </Row>
              ))}
            </tbody>
            <tfoot>
              <TotalRow>
                <Td strong>סה״כ</Td>
                <Td align="end" strong>{ils(d.revenue)}</Td>
                <Td align="end" strong>{ils(d.recognized_revenue)}</Td>
                <Td align="end" strong>{ils(payerCost)}</Td>
                <Td align="end" strong>{ils(d.recognized_revenue - payerCost)}</Td>
                <Td align="center" strong color={marginColor(((d.recognized_revenue - payerCost) / Math.max(d.recognized_revenue, 1)) * 100)}>
                  {pct2(((d.recognized_revenue - payerCost) / Math.max(d.recognized_revenue, 1)) * 100)}
                </Td>
              </TotalRow>
            </tfoot>
          </Scroll>
          {/* 🔴 Two cost bases on one screen, named out loud. The tile above uses
              the payroll engine; this table sums per-meeting tariffs. The gap is
              on-call, bonuses, non-clinical hours and no-show pay — real money
              that belongs to a person and not to any meeting. It used to live
              only in a tooltip, so a reader who added this column and compared it
              to the tile concluded one of them was wrong. */}
          <Footnote pad>
            {`העלות כאן היא סכום תעריפי הפגישות — ${ils(payerCost)}. אריח "עלויות מטפלים" מראה `}
            <b style={{ color: T.ink }}>{ils(d.therapist_cost)}</b>
            {` ממנוע השכר, והפרש של ${ils(Math.abs(d.therapist_cost - payerCost))} הוא כוננות, בונוסים, שעות לא-קליניות ותשלום על אי-הגעה — כסף אמיתי ששייך לאדם ולא לפגישה.`}
          </Footnote>
        </Card>

        <Card flush>
          <Title flush badge={<NotFiltered names={missed('by_service')} />} note="כל הקופות">רווחיות לפי סוג שירות</Title>
          <Scroll min={520}>
            <thead>
              <tr>
                <Th>סוג שירות</Th>
                <Th align="center">פגישות</Th>
                <Th align="end">הכנסות</Th>
                <Th align="end">עלות מטפלים</Th>
                <Th align="end">רווח</Th>
                <Th align="center">שיעור רווח</Th>
              </tr>
            </thead>
            <tbody>
              {d.by_service.map((s) => (
                <Row key={s.service}>
                  <Td strong>
                    {s.service}
                    {/* The share each intake line earns, said on the row itself —
                        Ohad, 01/11: the third and the two thirds must show here too. */}
                    {s.service === 'אינטייק מנהל טיפול' && (
                      <span style={{ fontSize: 11, fontWeight: 400, color: T.inkSoft, marginInlineStart: 6 }}>שליש מהמנוי</span>
                    )}
                    {s.service === 'אינטייק פסיכיאטר' && (
                      <span style={{ fontSize: 11, fontWeight: 400, color: T.inkSoft, marginInlineStart: 6 }}>שני שליש מהמנוי</span>
                    )}
                    {s.service === CLOSURE_SERVICE && (
                      <>
                        <span style={{ fontSize: 11, fontWeight: 400, color: T.inkSoft, marginInlineStart: 6 }}>שני שליש · לא הגיעו לפסיכיאטר</span>
                        <NotFiltered names={missed('oncall_closure')} />
                      </>
                    )}
                  </Td>
                  {/* 🔴 The closure row's count is subscriptions a doctor closed, not
                      meetings. It stays in the column so the row can be checked, and the
                      total below skips it (the trap of 19/10, a number under the wrong
                      header). */}
                  <Td align="center" soft title={s.service === CLOSURE_SERVICE ? 'סגירות, לא פגישות — אינן בסה״כ הפגישות' : undefined}>
                    {s.service === CLOSURE_SERVICE ? `${num(s.n)} סגירות` : num(s.n)}
                  </Td>
                  <Td align="end">{ils(s.revenue)}</Td>
                  <Td align="end" soft>{s.service === CLOSURE_SERVICE ? '—' : ils(s.cost)}</Td>
                  <Td align="end" strong>{ils(s.profit)}</Td>
                  <Td align="center" strong color={marginColor(s.margin)}>
                    {s.service === CLOSURE_SERVICE ? (
                      <span title="לסגירה אין עלות לפגישה — זמן הכונן הוא שכר כוננות, שכבר בתוך אריח עלויות המטפלים." style={{ cursor: 'help' }}>—</span>
                    ) : pct2(s.margin)}
                  </Td>
                </Row>
              ))}
            </tbody>
            {/* The rows above are what each service EARNED; the tile is what was
                BILLED. The row between them is the difference, and it is the finding:
                subscriptions charged whose psychiatrist leg is still owed (positive),
                or a window that earned two thirds sold earlier (negative — a closure
                batch does this). Without it the table does not reach the tile and
                reads as broken. */}
            <tfoot>
              {Math.abs(d.billed_not_recognized) >= 1 && (
                <tr style={{ background: T.orangeTint, borderTop: `1px solid ${T.border}` }}>
                  <Td color="#92400E" strong>
                    {d.billed_not_recognized > 0 ? 'חויב ועדיין לא הוכר' : 'הוכר ממנויים שחויבו קודם'}
                    <div style={{ fontSize: 11, fontWeight: 400, color: T.orangeInk, marginTop: 2 }}>
                      {d.billed_not_recognized > 0
                        ? `שני השליש של מנויים שהפסיכיאטר טרם ראה · ${num(d.intake_without_psy)} מתוך ${num(d.intake_without_psy + d.intake_with_psy)} מנויי הטווח לא הגיעו אליו`
                        : 'הטווח הכיר שני שליש של מנויים שנמכרו בטווחים קודמים'}
                    </div>
                  </Td>
                  <Td align="center" color={T.inkFaint}>—</Td>
                  <Td align="end" strong color="#92400E">{ils(d.billed_not_recognized)}</Td>
                  <Td colSpan={3} color="#B45309" style={{ fontSize: 11.5 }}>
                    {d.billed_not_recognized > 0
                      ? 'הכסף בחשבונית; יוכר כשהפסיכיאטר יראה את המטופל או כשכונן יסגור'
                      : 'ההפרש בין החשבונית של הטווח למה שהוכר בו'}
                  </Td>
                </tr>
              )}
              <TotalRow>
                <Td strong>סה״כ הוכר</Td>
                <Td align="center" strong soft>{num(d.by_service.filter((s) => s.service !== CLOSURE_SERVICE).reduce((a, s) => a + s.n, 0))}</Td>
                <Td align="end" strong>{ils(d.recognized_revenue)}</Td>
                <Td colSpan={3} soft style={{ fontSize: 11.5 }}>{`חויב ${ils(d.revenue)}`}</Td>
              </TotalRow>
            </tfoot>
          </Scroll>
        </Card>

      </div>

      {/* The same services again, kept apart by payer. Grouped and not flat: a flat
          table repeats the payer name fifteen times and the eye loses which block it
          is in. The payer is written once per block.
          Full width on purpose: at six columns it was the table whose last column
          got cut when all three stood side by side. */}
      {d.by_payer_service.length > 0 && (
          <Card flush>
            <Title flush badge={<NotFiltered names={missed('by_service')} />}>רווחיות לפי קופה × סוג שירות</Title>
            <Scroll min={560}>
              <thead>
                <tr>
                  <Th>קופה</Th>
                  <Th>סוג שירות</Th>
                  <Th align="center">פגישות</Th>
                  <Th align="end">הכנסות</Th>
                  <Th align="end">רווח</Th>
                  <Th align="center">שיעור רווח</Th>
                </tr>
              </thead>
              <tbody>
                {d.by_payer_service.map((r, i, all) => {
                  const firstOfPayer = i === 0 || all[i - 1].payer !== r.payer;
                  return (
                    <tr
                      key={`${r.payer}-${r.service}`}
                      className="tz-row"
                      style={{ borderTop: firstOfPayer ? `2px solid ${T.border}` : undefined }}
                    >
                      <Td strong>{firstOfPayer ? r.payer : ''}</Td>
                      <Td soft>{r.service}</Td>
                      <Td align="center" soft>{num(r.n)}</Td>
                      <Td align="end">{ils(r.revenue)}</Td>
                      <Td align="end" strong>{ils(r.profit)}</Td>
                      <Td
                        align="center" strong
                        color={r.revenue === 0 ? T.inkFaint : marginColor(r.margin)}
                        title={r.revenue === 0 ? 'הקופה אינה משלמת על השירות הזה — העבודה נעשית והעלות אמיתית' : undefined}
                      >
                        {r.revenue === 0 ? '—' : `${pct2(r.margin)}`}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Scroll>
          </Card>
      )}

      <div style={GRID(460)}>
      {/* ── unit cost: the arithmetic under the by-service table, shown so it can
          be checked. Medians across the active roster, not any one therapist. ── */}
      <Card flush>
        <Title
          flush badge={<NotFiltered names={missed('rates')} />}
          note="חציון על הרוסטר הפעיל"
          ask="העלות היא חציון על הרוסטר הפעיל. ההכנסה היא תעריף מכבי — 77% מההכנסות במסך — כי עמודה אחת אינה יכולה להחזיק שש קופות. שתי שורות האינטייק נושאות את חלקן בפיצול (שליש למנהל התיק, שני שליש לפסיכיאטר) ולא את המנוי כולו, כדי שהטבלה הזו והרווחיות לפי שירות יסכימו על מה שווה אינטייק."
        >
          כלכלת הפגישה — הכנסה מול עלות
        </Title>
        <Scroll min={620}>
          <thead>
            <tr>
              <Th>סוג שירות</Th>
              <Th align="center">פגישות</Th>
              <Th align="center">משך חציוני</Th>
              <Th align="end">הכנסה לפגישה</Th>
              <Th align="end">עלות לפגישה</Th>
              <Th align="end">הכנסה לשעה</Th>
              <Th align="end">עלות לשעה</Th>
              <Th align="center">מרווח</Th>
            </tr>
          </thead>
          <tbody>
            {d.rates.map((r) => (
              <Row key={r.service}>
                <Td strong>{r.service}</Td>
                <Td align="center" soft>{num(r.n)}</Td>
                <Td align="center">{r.median_minutes} דק׳</Td>
                <Td align="end">{ils(r.revenue)}</Td>
                <Td align="end" soft>{ils(r.cost)}</Td>
                <Td align="end" strong>{ils(r.revenue_per_hour)}</Td>
                {/* No conditional colour here. A ₪300 threshold used to paint the
                    psychiatrist hour red, one line above a footnote saying it is
                    expected to cost 3.5x a case manager's — red claiming a problem
                    that the panel itself explains away. The margin column carries
                    the judgement; this column carries the fact. */}
                <Td align="end" soft>{ils(r.cost_per_hour)}</Td>
                <Td align="center" strong color={marginColor(r.margin)}>{pct2(r.margin)}</Td>
              </Row>
            ))}
          </tbody>
        </Scroll>
        <Footnote pad>
          <b style={{ color: T.ink }}>שעת פסיכיאטר עולה פי 3.5 משעת מנהל תיק.</b>{' '}
          המשך נמדד מהיומן בפועל ולא מהאורך המתוכנן — האורכים שבמנוע השכר הם ברירות מחדל
          שמישהו הקליד, לא מדידה.
        </Footnote>
      </Card>
      </div>



      {/* ── who brought in what ───────────────────────────────────────────────
          Avi, 14/10: "איזה מטפלים מכניסים לנו הכי הרבה כסף... ומספר מטפל כמה כסף
          הוא מכניס לחברה". Ranked, biggest first, and capped at the top 15 with the
          rest summed into one line — a roster of 52 rendered whole turns the panel
          into a page nobody reads, and the tail is the part nobody acts on. */}
      {d.by_therapist.length > 0 && (
        <Card flush>
          <Title
            flush note={`${num(d.by_therapist.length)} מטפלים · ממוצע ${ils(revPerTherapist)} למטפל`}
            badge={<NotFiltered names={missed('by_therapist')} />}
            ask="ההכנסה היא מה שהמטפל חייב — לא מה שנשאר לחברה. המנוי האבחוני מתחלק באותו כלל שבטבלת הרווחיות לפי שירות: שליש למנהל התיק שביצע את האינטייק, שני שליש לפסיכיאטר שביצע את שלו. העלות היא חציון פר-פגישה ולא השכר בפועל — מנוע השכר משלם לאדם וכולל כוננות, בונוסים ושעות לא-קליניות, שאינן שייכות לשום פגישה."
          >
            הכנסה לפי מטפל
          </Title>
          <Scroll min={640}>
            <thead>
              <tr>
                {/* The rank in a cell of its own: a number glued to a Latin name
                    inside an RTL cell lands on either side of it (02/11). */}
                <Th align="center">#</Th>
                <Th>מטפל</Th>
                <Th align="center">פגישות</Th>
                <Th align="center">אינטייק</Th>
                <Th align="center">מעקבים</Th>
                <Th align="end">הכנסה</Th>
                <Th align="end">הוכר</Th>
                <Th align="end">לפגישה</Th>
                <Th align="end">עלות</Th>
                <Th align="center">מרווח</Th>
              </tr>
            </thead>
            <tbody>
              {d.by_therapist.slice(tPage * 10, tPage * 10 + 10).map((t, i) => (
                <Row key={t.therapist}>
                  <Td align="center" soft style={{ width: 36 }}>{tPage * 10 + i + 1}</Td>
                  <Td strong>{t.therapist}</Td>
                  <Td align="center" soft>{num(t.meetings)}</Td>
                  <Td align="center" soft>{num(t.cm_intake + t.psy_intake)}</Td>
                  <Td align="center" soft>{num(t.cm_fu + t.psy_fu)}</Td>
                  <Td align="end">{ils(t.revenue)}</Td>
                  {/* Billed on the case manager's intake, earned a third / two thirds:
                      a psychiatrist's intakes show here and not in the column before. */}
                  <Td align="end" strong title="שליש מהמנוי לאינטייק מנהל התיק, שני שליש לאינטייק הפסיכיאטר, מעקבים במלואם">{ils(t.recognized)}</Td>
                  <Td align="end">{ils(t.revenue_per_meeting)}</Td>
                  <Td align="end" soft>{ils(t.cost)}</Td>
                  <Td align="center" strong color={marginColor(t.margin)}>{pct2(t.margin)}</Td>
                </Row>
              ))}
            </tbody>
          </Scroll>
          {d.by_therapist.length > 10 && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '9px 18px', borderTop: `1px solid ${T.borderSoft}`,
              fontSize: 12, color: T.inkSoft,
            }}>
              <span>
                {num(tPage * 10 + 1)}–{num(Math.min(tPage * 10 + 10, d.by_therapist.length))} מתוך {num(d.by_therapist.length)}
              </span>
              <span style={{ display: 'flex', gap: 6 }}>
                <PageBtn disabled={tPage === 0} onClick={() => setTPage(tPage - 1)}>הקודם</PageBtn>
                <PageBtn
                  disabled={(tPage + 1) * 10 >= d.by_therapist.length}
                  onClick={() => setTPage(tPage + 1)}
                >
                  הבא
                </PageBtn>
              </span>
            </div>
          )}
          <Footnote pad>
            הסכום כאן הוא מה שכל מטפל הרוויח — שליש מהמנוי באינטייק מנהל התיק, שני
            שליש באינטייק הפסיכיאטר, מעקבים במלואם — ולא החשבונית. הוא אינו מגיע
            להכנסות הכוללות בכוונה: ההפרש ({ils0(d.billed_not_recognized)}) הוא מנויים
            שחויבו והפסיכיאטר טרם ראה, וסגירות כונן אינן שייכות למטפל.
            {d.excluded_meetings > 0 && (
              <> פגישות של פרטי ותרומה נספרות כאן עם הכנסה אפס — העבודה נעשתה, התשלום אינו נקשר לפגישה.</>
            )}
          </Footnote>
        </Card>
      )}

      {/* ── utilization: opened hours against what they produced ──────────────── */}
      <div style={GRID(380)}>
        <Card flush>
          <Title
            flush badge={<NotFiltered names={missed('utilization')} />}
            note="ימים שחלפו בחודש בלבד"
            ask="ניצולת = (בוצעו + אי-הגעה) ÷ נפתחו. אי-הגעה צרכה את השעה ומשולמת, ולכן היא נחשבת מנוצלת — אותה הגדרה בדיוק שבמסך כוח אדם, ואסור שתיפרד. 'לא נוצלו' אינו 'פנוי': שעה חסומה ושעה פתוחה-ולא-מוזמנת נראות כאן זהה."
          >
            ניצולת קלינית ושעות עבודה
          </Title>
          <Scroll min={560}>
            <thead>
              <tr>
                <Th>תפקיד</Th>
                <Th align="center">מטפלים</Th>
                <Th align="end">נפתחו</Th>
                <Th align="end">בוצעו</Th>
                <Th align="end">אי-הגעה</Th>
                <Th align="end">לא נוצלו</Th>
                <Th align="center">ניצולת</Th>
              </tr>
            </thead>
            <tbody>
              {d.utilization.map((u) => (
                <Row key={u.role}>
                  <Td strong>{u.role}</Td>
                  <Td align="center" soft>{u.therapists}</Td>
                  <Td align="end">{u.given_hours.toFixed(2)}</Td>
                  <Td align="end">{u.done_hours.toFixed(2)}</Td>
                  <Td align="end" soft>{u.no_show_hours.toFixed(2)}</Td>
                  <Td align="end" color={T.red}>{u.idle_hours.toFixed(2)}</Td>
                  <Td align="center" strong color={u.util_pct >= 80 ? T.green : u.util_pct >= 65 ? T.orange : T.red}>
                    {pct2(u.util_pct)}
                  </Td>
                </Row>
              ))}
            </tbody>
          </Scroll>
        </Card>

        {/* The question Avi actually asked: "בכללית אנחנו תמיד 50% ניצולת, אין לנו
            מספיק אקשן מכללית". The hours table has no payer column, so the therapist
            is the bridge — their declared hours are divided between the payers they
            served, in proportion to the minutes delivered to each. That proportion is
            an ALLOCATION and the panel says so. */}
        {d.utilization_payer.length > 0 && (
          <Card flush>
            <Title
              flush badge={<NotFiltered names={missed('util_payer')} />}
              note="ימים שחלפו בחודש בלבד"
              ask="לטבלת השעות אין עמודת קופה. שעות שנפתחו מחולקות בין הקופות לפי חלקן בדקות שנמסרו בפועל — זו הקצאה ולא מדידה: מטפל פותח יומן אחד, לא יומן לכל קופה. ⇒ המספר עונה על 'כמה תפוסים המטפלים שאליהם שולחים מטופלי הקופה הזאת', ולא על 'כמה שעות הקופה מחזיקה ריקות'."
            >
              ניצולת לפי קופה
            </Title>
            <Scroll min={420}>
              <thead>
                <tr>
                  <Th>קופה</Th>
                  <Th align="end">שעות שנפתחו</Th>
                  <Th align="end">שעות שנמסרו</Th>
                  <Th align="center">ניצולת</Th>
                </tr>
              </thead>
              <tbody>
                {d.utilization_payer.map((u) => (
                  <Row key={u.payer}>
                    <Td strong>{u.payer}</Td>
                    <Td align="end">{u.given_hours.toFixed(2)}</Td>
                    <Td align="end">{u.done_hours.toFixed(2)}</Td>
                    <Td align="center" strong color={u.pct >= 80 ? T.green : u.pct >= 65 ? T.orange : T.red}>
                      {pct2(u.pct)}
                    </Td>
                  </Row>
                ))}
              </tbody>
            </Scroll>
            <Footnote pad>
              אחוז מעל 100 אינו שגיאה: היומן מרשה לקבוע למטפל מחוץ לשעות שהצהיר,
              ו-11 מתוך 49 מוזמנים מעבר להן.
            </Footnote>
          </Card>
        )}
      </div>

      {/* ── lifetime value per patient, per payer ─────────────────────────────── */}
      <Card flush>
        <Title
          flush badge={<NotFiltered names={missed('ltv')} />}
          note="מי שהתחיל טיפול בפועל"
          ask="האוכלוסייה: מטופלים שהאינטייק של מנהל התיק שלהם בוצע. רישום בלבד אינו נספר. 'הגיעו לפסיכיאטר' = כמה מהם עשו גם אינטייק פסיכיאטרי. 'מנוי' = המנוי האבחוני, תעריף אחד לקופה; 'מעקבים' = ממוצע המעקבים כפול התעריף. ITC מחושב אחרת: אין מנוי, עמודת המנוי שם היא האינטייק הפסיכיאטרי פר פגישה. כל ההיסטוריה, לא התקופה שנבחרה."
        >
          יחידת כלכלת מטופל · כל מהלך הטיפול
        </Title>
        <Scroll min={760}>
          <thead>
            <tr>
              <Th>קופה</Th>
              <Th align="center">אינטייק מנהל תיק</Th>
              <Th align="center">הגיעו לפסיכיאטר</Th>
              <Th align="center">מעקבי CM</Th>
              <Th align="center">מעקבי פסיכ׳</Th>
              <Th align="end">מנוי</Th>
              <Th align="end">מעקבים</Th>
              <Th align="end">הוכר</Th>
              <Th align="end">עלות</Th>
              <Th align="end">רווח</Th>
              <Th align="center">שיעור</Th>
            </tr>
          </thead>
          <tbody>
            {d.ltv.map((l) => (
              <Row key={l.payer}>
                <Td strong>{l.payer}</Td>
                <Td align="center">{num(l.patients)}</Td>
                <Td align="center">
                  {num(l.reached_psy)}
                  <span style={{ color: T.inkFaint, fontSize: 11, marginInlineStart: 6 }}>{pct2(l.reached_psy_pct)}</span>
                </Td>
                <Td align="center">{l.avg_cm_fu.toFixed(2)}</Td>
                <Td align="center">{l.avg_psy_fu.toFixed(2)}</Td>
                <Td align="end">{ils(l.subscription)}</Td>
                <Td align="end">{ils(l.followup_revenue)}</Td>
                <Td align="end" title="שליש מהמנוי לכל מטופל, שני שליש לחלק שהגיע לפסיכיאטר, מעקבים במלואם">{ils(l.recognized)}</Td>
                <Td align="end" soft>{ils(l.cost)}</Td>
                <Td align="end" strong>{ils(l.profit)}</Td>
                <Td align="center" strong color={l.margin >= 70 ? T.green : l.margin >= 50 ? T.orange : T.red}>
                  {pct2(l.margin)}
                </Td>
              </Row>
            ))}
          </tbody>
        </Scroll>
      </Card>

      {/* ── what this screen cannot answer yet, and why ───────────────────────── */}
      <div style={GRID(320)}>
        <Blocked
          title="רווחיות לפי קופה — עלות מדודה"
          why="מנוע השכר משלם לפי מטפל, לא לפי קופה. צריך כלל ייחוס — לפי פגישות? לפי דקות? ומה עם כוננות ובונוס, שאינם שייכים לאף קופה."
        />
        <Blocked
          title="גשר רווח תפעולי (EBITDA)"
          why="דורש opex וספקים בפועל, חודש בחודשו. בתוכנית העסקית הם קיימים כיעד בלבד, ואבי ביקש מספר חודשי אחד מה-CFO ולא פירוט."
        />
      </div>
    </div>
  );
}
